import { AfterViewInit, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';

import { Subscription } from 'rxjs';

import { BarcodeReader, SdkError } from "@pixelverse/strichjs-sdk";

import * as Sentry from '@sentry/browser';

import { ModalService } from '../../services/modal.service';
import { ScannerService } from '../../services/scanner.service';
import {ToolbarService} from '../../services/toolbar.service';

@Component({
  selector: 'app-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss']
})
export class ScannerComponent implements AfterViewInit, OnDestroy {

  // emits an SdkError (init failed) or null (init succeeded) after BarcodeReader initialization
  @Output()
  initialized = new EventEmitter<SdkError | null>();

  @ViewChild('scannerHost')
  private scannerHostElem: ElementRef<HTMLElement>;

  private scannerInactivityTimer: number;
  private barcodeReader?: BarcodeReader = undefined;
  private barcodeReaderInitializePromise?: Promise<void>;

  private scanningPausedSubscription: Subscription;
  private initSubscription: Subscription;

  constructor(private scannerService: ScannerService,
              private modalService: ModalService,
              private toolbar: ToolbarService,
              private ngZone: NgZone) {
  }

  ngAfterViewInit(): void {
    console.debug(`ScannerComponent.ngAfterViewInit()`);
    // initialize BarcodeReader as the SDK is initialized
    this.initSubscription = this.scannerService.sdkInitialized.subscribe((initialized) => {
        if (initialized) {
          setTimeout(() => { // defer to next run, to avoid https://angular.dev/errors/NG0100
            try {
              this.barcodeReaderInitializePromise = this.setupBarcodeReader();
              this.ngZone.run(() => this.initialized.emit(null));
            } catch (e) {
              this.ngZone.run(() => this.initialized.emit(e));
            }
          });
        }
      });
  }

  ngOnDestroy() {
    console.debug(`ScannerComponent.ngOnDestroy()`);
    this.initSubscription?.unsubscribe();
    if (this.barcodeReaderInitializePromise) {
      this.barcodeReaderInitializePromise.finally(() => {
        console.debug(`Tearing down BarcodeReader...`);
        this.clearScannerInactivityTimer();
        return this.tearDownBarcodeReader();
      });
    }
  }

  private async setupBarcodeReader() {
    console.debug(`Creating BarcodeReader...`);
    const hostElement = this.scannerHostElem.nativeElement;
    const config = this.scannerService.strichConfiguration(hostElement);
    console.debug(`Using STRICH SDK config:`, config);

    let barcodeReader: BarcodeReader;
    try {
      barcodeReader = new BarcodeReader(config);
    } catch (err) {
      console.error(`< BarcodeReader.ctor() failed with error: ${err.message}`);
      Sentry.captureException(err);
      throw err;
    }

    // do the initialization outside of the Angular zone
    await this.ngZone.runOutsideAngular(async () => {
      console.debug('> BarcodeReader.initialize()');
      try {
        this.toolbar.setNavigationBlocked(true);
        await barcodeReader.initialize();
        console.debug(`< BarcodeReader.initialize() completed successfully`);
        this.barcodeReader = barcodeReader;
        this.barcodeReader.detected = (detections) => {
          this.ngZone.run(() => {
            this.resetScannerInactivityTimer();
            if (detections && detections.length > 0) {
              this.scannerService.publishScanResult(detections[0]);
            }
          });
        };
        this.barcodeReader.onError = (err) => Sentry.captureException(err);

        // subscribe to lifecycle updates
        this.scanningPausedSubscription = this.scannerService.scanningPaused.subscribe((v) => this.onScannerPaused(v));
      } catch (err) {
        console.error(`< BarcodeReader.initialize() failed with error: ${err.message}`);
        if (!err.duringCameraAccess) {
          Sentry.captureException(err);
        }
      } finally {
        this.toolbar.setNavigationBlocked(false);
      }
    });
  }

  /**
   * Inverse of setupBarcodeReader(): halt barcode scanning, release resources.
   * @private
   */
  private async tearDownBarcodeReader() {
    if (!this.barcodeReader) {
      return;
    }

    this.toolbar.setNavigationBlocked(true);
    try {
      // clear pending timeouts
      this.clearScannerInactivityTimer();

      // clear subscriptions
      this.scanningPausedSubscription?.unsubscribe();
      this.scanningPausedSubscription = undefined;

      // tear down barcode reader
      try {
        console.debug(`> BarcodeReader.stop()`);
        await this.barcodeReader.stop();
        console.debug(`< BarcodeReader.stop()`);
      } catch (e) {
        console.error(`< BarcodeReader.stop() failed, ignoring`, e);
      }

      try {
        console.debug(`> BarcodeReader.destroy()`);
        await this.barcodeReader.destroy();
        console.debug(`< BarcodeReader.destroy()`);
      } catch (e) {
        console.error(`< BarcodeReader.destroy() failed, ignoring`, e);
      }
    } finally {
      this.barcodeReader = undefined;
      this.toolbar.setNavigationBlocked(false);
    }
  }

  private async onScannerPaused(paused: boolean) {
    console.debug(`Scanner paused state is now: ${paused}`);

    // ignore lifecycle callbacks when we do not have a BarcodeReader
    if (this.barcodeReader === undefined) {
      console.debug(`Ignoring scanner lifecycle change (no BarcodeReader)`);
      return;
    }

    if (paused) {
      console.debug(`Pausing scanning...`);
      this.pauseScanning();
    } else {
      console.debug(`Resuming scanning...`);
      this.resumeScanning().then(() => {
        console.debug(`Scanning resumed`);
      });
    }
  }

  private startScannerInactivityTimer(): void {
    const timeoutMillis = this.scannerService.scannerInactivityTimeout;
    if (!timeoutMillis) {
      console.debug(`Inactivity timeout is disabled disabled`);
      return;
    }
    if (this.scannerInactivityTimer) {
      console.debug(`Resetting inactivity timer...`);
      this.resetScannerInactivityTimer();
    } else {
      console.debug(`Starting inactivity timer (${timeoutMillis} ms)...`);
      // @ts-ignore
      this.scannerInactivityTimer = setTimeout(() => this.onInactivityTimeout(), timeoutMillis);
    }
  }

  private clearScannerInactivityTimer(): void {
    if (this.scannerInactivityTimer) {
      console.debug(`Clearing scanner inactivity timer`);
      clearTimeout(this.scannerInactivityTimer);
      this.scannerInactivityTimer = undefined;
    }
  }

  private resetScannerInactivityTimer(): void {
    this.clearScannerInactivityTimer();
    this.startScannerInactivityTimer();
  }

  private pauseScanning(): void {
    this.clearScannerInactivityTimer();
    console.debug(`> BarcodeReader.stop(false)`);
    this.barcodeReader.stop()
      .then(() => {
        console.debug(`< BarcodeReader.stop()`);
      })
      .catch((err: SdkError) => {
        Sentry.captureException(err);
        console.error(`< BarcodeReader.stop() failed with error: ${err.message}`);
      });
  }

  private async onInactivityTimeout() {
    if (!this.barcodeReader) {
      console.debug(`Ignoring scanner inactivity timeout (no BarcodeReader)`);
      return;
    }

    console.debug(`Disabling scanner due to inactivity timeout`);
    await this.tearDownBarcodeReader();

    this.ngZone.run(() => {
      this.modalService.showScannerPausedModal(() => {
        console.debug(`Enabling scanner after inactivity timeout`);
        this.setupBarcodeReader();
      });
    });
  }

  private async resumeScanning(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        this.doResumeScanning()
          .then(() => resolve())
          .catch((err) => reject(err));
      });
    });
  }

  private async doResumeScanning(): Promise<void> {
    console.debug(`> BarcodeReader.start()`);
    try {
      await this.barcodeReader.start();
      console.debug(`< BarcodeReader.start() succeeded`);
      this.resetScannerInactivityTimer();
    } catch (error) {
      console.error(`< BarcodePicker.start() failed: ${error}`);
      if (!error.duringCameraAccess) {
        Sentry.captureException(error);
      }
    }
  }
}
